import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {emitEventLog} from '@di-shared/features/analytics';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import ConsentBannerKr from '@di-shared/features/home/kr/ConsentBanner.kr';
import ConsentBannerLoadingKr from '@di-shared/features/home/kr/ConsentBannerLoading.kr';
import MileageChallengeSwitcher from '@di-shared/features/mileage/ecomileage-v2/MileageChallengeSwitcher';
import {
  MileageChallengeError,
  MileageChallengeLoading,
  MileageChallengeOdometerError,
} from '@di-shared/features/mileage/ecomileage/MileageChallenge';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';

interface EcoMileagePageProps {
  vin: string;
}

function EcoMileagePage({vin}: EcoMileagePageProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T40049')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <ErrorBoundary fallback={<></>}>
          <Suspense fallback={<ConsentBannerLoadingKr />}>
            <ConsentBannerKr vin={vin} />
          </Suspense>
        </ErrorBoundary>
        <div
          className="tabbar_txt tabbar_sticky"
          role="tablist"
        >
          <div className="inner">
            <button
              type="button"
              className="tabbar_item body_large_r is-selected"
              onClick={() => {
                emitEventLog({
                  event: 'eco_mileage_interaction',
                  data_action: 'tab click',
                  data_name: '정기마일리지',
                });
                navigate(ROUTE_PATHS.MILEAGE_ECO.getFullPath(vin), {replace: true});
              }}
            >
              <span className="txt">{t('T40053')}</span>
            </button>
            <button
              type="button"
              className="tabbar_item body_large_r"
              onClick={() => {
                emitEventLog({
                  event: 'eco_mileage_interaction',
                  data_action: 'tab click',
                  data_name: '운전마일리지',
                });
                navigate(ROUTE_PATHS.MILEAGE_SAFE_DRIVING.getFullPath(vin), {replace: true});
              }}
            >
              <span className="txt">{t('T30253')}</span>
            </button>
            <button
              type="button"
              className="tabbar_item body_large_r"
              onClick={() => {
                emitEventLog({
                  event: 'eco_mileage_interaction',
                  data_action: 'tab click',
                  data_name: '적립 내역',
                });
                navigate(ROUTE_PATHS.MILEAGE_BALANCE.getFullPath(vin), {replace: true});
              }}
            >
              <span className="txt">{t('T30177')}</span>
            </button>
          </div>
        </div>
        <div className="tab_content regular_mileage">
          <section className="section section_regular_mileage">
            <ErrorBoundary fallback={<MileageChallengeError />}>
              <ErrorBoundary
                fallback={
                  <Suspense fallback={<MileageChallengeLoading />}>
                    <MileageChallengeOdometerError vin={vin} />
                  </Suspense>
                }
              >
                <Suspense fallback={<MileageChallengeLoading />}>
                  <MileageChallengeSwitcher vin={vin} />
                </Suspense>
              </ErrorBoundary>
            </ErrorBoundary>
            <div className="driving_banner type_square">
              <LinkTo
                to={ROUTE_PATHS.GUIDE_MILEAGE.getFullPath()}
                className="driving_banner_box"
              >
                <span className="driving_banner_txtbox type_reverse">
                  <strong className="driving_banner_title body_large_b">{t('T30254')}</strong>
                  <span className="driving_banner_desc body_small_r">{t('T30255')}</span>
                </span>
                <span className="driving_banner_imgbox">
                  <ImageSelector
                    className="driving_banner_img"
                    alt="driving_banner_img"
                    kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_guide.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_guide.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_guide.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_guide.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_guide.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_guide.png')}
                  />
                </span>
              </LinkTo>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

export default EcoMileagePage;
